import { Injectable, OnDestroy, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, catchError, filter, map, of, switchMap, timer } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { RolesMenuService } from 'src/app/apps/nodo/modules/administracion/services/roles-menus.service';
import { PaquetesDocumentalesService } from 'src/app/apps/nodo/modules/paquetes-documentales/services/paquetes-documentales.service';
import { IPaqueteDocumental } from 'src/app/apps/nodo/modules/paquetes-documentales/interfaces/paquete-documental';
import { NotificacionesVistasService } from 'src/app/shared/services/notificaciones-vistas.service';

/** Intervalo de polling en milisegundos. */
const POLL_INTERVAL_MS = 30_000;

/** ID del portal Nodo. */
const PORTAL_NODO = '3';

/** Prefijo de las rutas del portal Nodo. */
/**
 * La app autenticada. El sondeo vive en todo /app y no solo en /app/nodo: el aviso
 * tiene que llegar aunque el usuario se haya ido a otra pantalla, que es justo para
 * lo que existe la campanita. Fuera de /app (login) no se sondea nada.
 */
const PREFIJO_APP = '/app';

/**
 * Ruta del menú que habilita el monitor. Si el rol no tiene este menú, no puede
 * ver la pantalla de paquetes documentales y el sondeo no arranca nunca.
 */
const RUTA_MENU_HABILITADOR = 'app/nodo/juridica/paquetes-documentales';

/**
 * Avisa en la campanita del topbar de los paquetes documentales terminados y sin
 * descargar.
 *
 * La persistencia sale del backend: se pregunta por paquetes Completados con
 * fechaDescarga nula, así que el aviso sobrevive a recargar la página, cerrar
 * sesión y cambiar de equipo, sin guardar nada en el navegador (salvo el set de
 * "avisados" en memoria, sólo para no repetir el toast del mismo paquete).
 *
 * NO dispara ninguna petición al arrancar la aplicación ni durante el login:
 * todo cuelga del `effect` del constructor, que sólo enciende el sondeo cuando
 * ya hay portal Nodo, ruta de Nodo y permiso resueltos. El permiso mismo se
 * consulta perezosamente, en el primer contexto válido, no al arrancar.
 */
@Injectable({ providedIn: 'root' })
export class PaquetesDocumentalesMonitorService implements OnDestroy {
    private readonly service = inject(PaquetesDocumentalesService);
    private readonly rolesMenuService = inject(RolesMenuService);
    private readonly toastr = inject(ToastrService);
    private readonly router = inject(Router);
    private readonly vistas = inject(NotificacionesVistasService);

    /** Si la pantalla que se está viendo pertenece al portal Nodo. */
    private readonly enApp = signal<boolean>(false);

    /**
     * Si el rol tiene el menú que habilita el monitor. null = todavía no se
     * consultó. Se resuelve una sola vez por sesión y se conserva: es información
     * del rol, no de la pantalla activa.
     */
    private readonly permiso = signal<boolean | null>(null);

    /** Evita repetir la consulta de menús mientras la primera está en vuelo. */
    private permisoEnConsulta = false;

    /** Paquetes Completados con fechaDescarga nula, listos para mostrarse en la campanita. */
    readonly pendientes = signal<IPaqueteDocumental[]>([]);

    /** Suscripción al intervalo de polling. */
    private sub: Subscription | null = null;

    /** Ids ya avisados por toast, para no repetir el mismo aviso en cada tick. */
    private avisados = new Set<number>();

    constructor() {
        this.enApp.set(this.esRutaApp(this.router.url));
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => this.enApp.set(this.esRutaApp(e.urlAfterRedirects)));

        // Reactivo: arranca/detiene el polling según contexto y permiso.
        effect(() => {
            const enApp = this.enApp();
            const permiso = this.permiso();

            // Ya no se exige estar en el portal Nodo ni en una ruta de Nodo. Se exigía,
            // y por eso la campanita no avisaba nunca: al salir de /app/nodo el sondeo
            // se detenía y la lista se vaciaba, justo en el escenario para el que la
            // alerta existe (irse a otra pantalla y volver). El permiso del menú sigue
            // siendo la autorización real; el portal solo decide qué barra lateral se ve.
            if (!enApp) {
                this.detener();
                return;
            }

            // El permiso se consulta al primer contexto válido, no al arrancar la app:
            // así quien nunca entra a la app autenticada no gasta ni esa petición.
            if (permiso === null) {
                this.resolverPermiso();
                this.detener();
                return;
            }

            if (!permiso) {
                this.detener();
                this.pendientes.set([]);   // sin permiso no hay nada que mostrar
                return;
            }

            this.arrancar();
        });
    }

    ngOnDestroy(): void {
        this.detener();
    }

    /**
     * Quita un paquete de la lista de pendientes (al hacer clic en la campanita).
     * Si el paquete sigue sin descargarse, el siguiente sondeo lo vuelve a traer;
     * no hay estado local que "recuerde" el descarte de forma permanente porque
     * la fuente de verdad es el backend.
     */
    /** Etiqueta del periodo: un mes como 2026-08, varios resumidos por cantidad. */
    private periodo(p: IPaqueteDocumental): string {
        const lista = (p.meses ?? '')
            .split(',')
            .map((m) => Number(m.trim()))
            .filter((m) => m >= 1 && m <= 12);

        if (lista.length === 1) return `${p.anio}-${String(lista[0]).padStart(2, '0')}`;
        if (lista.length > 1) return `${p.anio} (${lista.length} meses)`;
        return `${p.anio}`;
    }

    /**
     * El usuario leyó el aviso: se registra en el backend para que NO vuelva a aparecer,
     * ni en este sondeo ni en la próxima sesión. Antes solo se filtraba el arreglo en
     * memoria y el siguiente tick de 30 s lo devolvía, porque el paquete seguía sin
     * descargar; ese era el aviso que no se iba nunca.
     */
    marcarVisto(id: number): void {
        this.pendientes.set(this.pendientes().filter((p) => p.id !== id));
        this.avisados.add(id);
        this.vistas.marcarVisto('PaqueteDocumental', String(id)).subscribe();
    }

    private esRutaApp(url: string): boolean {
        return (url ?? '').startsWith(PREFIJO_APP);
    }

    /**
     * Consulta los menús del portal Nodo del rol y decide si el monitor aplica.
     * Ante un fallo de red se asume que NO tiene permiso: un sondeo que no
     * debería existir es peor que una campanita que no aparece, y la próxima
     * navegación al Nodo vuelve a intentarlo.
     */
    private resolverPermiso(): void {
        if (this.permisoEnConsulta) return;
        this.permisoEnConsulta = true;

        this.rolesMenuService
            .getMenusbyId(PORTAL_NODO)
            .pipe(catchError(() => of(null)))
            .subscribe((resp) => {
                this.permisoEnConsulta = false;
                if (!resp) {
                    this.permiso.set(false);
                    return;
                }
                this.permiso.set(this.contieneMenuHabilitador(resp.data ?? []));
            });
    }

    /** Busca la ruta habilitadora en el árbol de menús (hasta tres niveles, como el sidebar). */
    private contieneMenuHabilitador(menus: any[]): boolean {
        for (const menu of menus ?? []) {
            const ruta = (menu?.urlRuta ?? '').trim();
            if (ruta === RUTA_MENU_HABILITADOR) return true;
            if (menu?.items?.length && this.contieneMenuHabilitador(menu.items)) return true;
        }
        return false;
    }

    private arrancar(): void {
        if (this.sub) return;
        // timer(0, ...) y no interval(...): interval emite recién al cumplirse el
        // periodo, así que al entrar la campanita quedaba vacía 30 segundos.
        this.sub = timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => this.service.listar(10, true).pipe(catchError(() => of(null)))),
                switchMap((res) => {
                    if (!res) return of(null);
                    const listos = (res.data ?? []).filter((p) => p.estado === 'Completado' && !p.fechaDescarga);
                    if (listos.length === 0) return of([] as IPaqueteDocumental[]);

                    // Un paquete que el usuario nunca descargue sigue "listo" para siempre, así que
                    // sin este filtro el aviso se regenera en cada tick. El backend dice cuáles no
                    // se han leído todavía; el aviso leído no vuelve aunque el paquete siga ahí.
                    return this.vistas
                        .pendientes(
                            'PaqueteDocumental',
                            listos.map((p) => ({ clave: String(p.id) }))
                        )
                        .pipe(map((claves) => listos.filter((p) => claves.includes(String(p.id)))));
                })
            )
            .subscribe((listos) => {
                if (!listos) return;
                for (const p of listos) {
                    if (!this.avisados.has(p.id)) {
                        this.avisados.add(p.id);
                        this.toastr.success(
                            `Paquete ${this.periodo(p)} listo para descargar.`,
                            'Paquete documental'
                        );
                    }
                }
                this.pendientes.set(listos);
            });
    }

    /**
     * Detiene el sondeo SIN olvidar lo ya conocido. Antes vaciaba `pendientes`, con lo
     * que la campanita se quedaba en blanco en cuanto el contexto dejaba de aplicar —
     * perdiendo el aviso que el usuario todavía no había visto. Vaciar la lista es una
     * decisión aparte, y sólo corresponde cuando el usuario no tiene permiso.
     */
    private detener(): void {
        this.sub?.unsubscribe();
        this.sub = null;
    }
}
